import { useEffect, useState } from "react";
import Nav from "../components/Nav";
import { api, fmt } from "../lib/api";
import { Link, useNavigate } from "react-router-dom";
import { TrendUp, TrendDown, ArrowRight } from "@phosphor-icons/react";

export default function Explore() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/stocks/popular");
        const enriched = await Promise.all(data.symbols.map(async (s) => {
          try {
            const [q, o] = await Promise.all([api.get(`/stocks/quote/${s}`), api.get(`/stocks/overview/${s}`)]);
            return { ...q.data, overview: o.data };
          } catch { return null; }
        }));
        setRows(enriched.filter(Boolean));
      } catch (e) {
        if (e.response?.status === 401) nav("/", { replace: true });
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-paper">
      <Nav />
      <main className="max-w-[1400px] mx-auto px-6 py-8">
        <div className="mb-8">
          <div className="label-eyebrow">Explore</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-1">Popular stocks, by profit margin.</h1>
          <p className="text-ink2 mt-3 max-w-2xl">Sorted quality companies. Higher net margin = more profit per dollar of revenue — usually a sign of pricing power.</p>
        </div>

        <div className="border border-black/10 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-ink text-paper text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Symbol</th>
                <th className="text-right px-4 py-3">Price</th>
                <th className="text-right px-4 py-3">Day</th>
                <th className="text-right px-4 py-3">Gross</th>
                <th className="text-right px-4 py-3">Operating</th>
                <th className="text-right px-4 py-3">Net</th>
                <th className="text-right px-4 py-3">P/E</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="font-mono-num">
              {rows.sort((a, b) => (b.overview?.netMargin || 0) - (a.overview?.netMargin || 0)).map((r) => {
                const up = (r.changePercent ?? 0) >= 0;
                return (
                  <tr key={r.symbol} className="border-t border-black/10 hover:bg-paper2" data-testid={`explore-row-${r.symbol}`}>
                    <td className="px-4 py-3">
                      <div className="font-bold">{r.symbol}</div>
                      <div className="text-xs text-ink2 font-sans">{r.name}</div>
                    </td>
                    <td className="text-right px-4 py-3">{fmt.money(r.price)}</td>
                    <td className={`text-right px-4 py-3 ${up ? "text-pos" : "text-neg"} flex items-center justify-end gap-1`}>
                      {up ? <TrendUp size={12} weight="bold" /> : <TrendDown size={12} weight="bold" />}
                      {fmt.pct(r.changePercent)}
                    </td>
                    <td className="text-right px-4 py-3">
                      <MarginBar value={r.overview?.grossMargin} color="#002FA7" />
                    </td>
                    <td className="text-right px-4 py-3">
                      <MarginBar value={r.overview?.operatingMargin} color="#3B82F6" />
                    </td>
                    <td className="text-right px-4 py-3">
                      <MarginBar value={r.overview?.netMargin} color="#93C5FD" />
                    </td>
                    <td className="text-right px-4 py-3">{r.overview?.peRatio ? r.overview.peRatio.toFixed(1) : "—"}</td>
                    <td className="text-right px-4 py-3">
                      <Link to={`/stock/${r.symbol}`} className="text-ikb hover:underline inline-flex items-center gap-1 text-xs">
                        Open <ArrowRight size={12} />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}

function MarginBar({ value, color }) {
  const v = Math.max(0, Math.min(100, value ?? 0));
  return (
    <div className="flex items-center gap-2 justify-end">
      <div className="w-20 h-2 bg-paper2 relative">
        <div className="absolute inset-y-0 left-0" style={{ width: `${v}%`, background: color }} />
      </div>
      <span className="w-14 text-right">{v.toFixed(1)}%</span>
    </div>
  );
}
